import { base44 } from '@/api/base44Client';

export const RANKING_TEAMS = [
  {
    key: 'worship',
    label: 'Louvor',
    memberEntity: 'WorshipTeamMember',
    scheduleEntity: 'WorshipSchedule',
    scheduleQueryKey: 'worshipSchedules',
    timeField: 'rehearsal_time',
    timeLabel: 'Ensaio'
  },
  {
    key: 'media',
    label: 'Mídia',
    memberEntity: 'MediaTeamMember',
    scheduleEntity: 'MediaSchedule',
    scheduleQueryKey: 'mediaSchedules',
    timeField: 'rehearsal_time',
    timeLabel: 'Ensaio'
  },
  {
    key: 'dance',
    label: 'Dança MBV',
    memberEntity: 'DanceTeamMember',
    scheduleEntity: 'DanceSchedule',
    scheduleQueryKey: 'danceSchedules',
    timeField: 'rehearsal_time',
    timeLabel: 'Ensaio'
  },
  {
    key: 'reception',
    label: 'Recepção',
    memberEntity: 'ReceptionTeamMember',
    scheduleEntity: 'ReceptionSchedule',
    scheduleQueryKey: 'receptionSchedules',
    timeField: 'arrival_time',
    fallbackTimeField: 'rehearsal_time',
    timeLabel: 'Chegada'
  },
  {
    key: 'kids',
    label: 'Kids MBV',
    memberEntity: 'KidsMBVMember',
    scheduleEntity: 'KidsMBVSchedule',
    scheduleQueryKey: 'kidsSchedules',
    timeField: 'arrival_time',
    timeLabel: 'Chegada'
  }
];

export const POINT_RULES = {
  scheduled: 3,
  confirmed: 2,
  checkinLate: 2,
  checkinOnTime: 3,
  checkinEarly: 4
};

export const ACTIVE_QR_NAME = 'ranking-mbv-checkin';
export const RANKING_CHECKIN_URL_ORIGIN = 'https://www.igrejambv.com.br';
export const RANKING_CHECKIN_PATH = '/RankingMBV';
export const ANDROID_APP_PACKAGE = 'com.mbv.igreja';
export const MOBILE_DEEP_LINK_SCHEME = 'com.mbv.igreja';
export const RANKING_START_SETTING_KEY = 'ranking_start_date';
export const CHECKIN_LOCATION_RADIUS_METERS = 50;
export const CHECKIN_TIME_WINDOW_MINUTES = {
  before: 30,
  after: 240
};

const normalize = (value) => String(value || '').trim().toLowerCase();
const dateOnly = (value) => String(value || '').slice(0, 10);
const localDateOnly = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export function getTeamByKey(teamKey) {
  return RANKING_TEAMS.find((team) => team.key === teamKey) || null;
}

export function buildMemberKey(member = {}) {
  return [
    member.member_id && `member:${member.member_id}`,
    member.email && `email:${normalize(member.email)}`,
    member.member_email && `email:${normalize(member.member_email)}`,
    member.id && `team:${member.id}`,
    member.name && `name:${normalize(member.name)}`,
    member.full_name && `name:${normalize(member.full_name)}`
  ].filter(Boolean);
}

export function samePerson(left = {}, right = {}) {
  const leftKeys = new Set(buildMemberKey(left));
  return buildMemberKey(right).some((key) => leftKeys.has(key));
}

export function resolveDisplayName(teamMember = {}, churchMember = {}) {
  return teamMember?.name || teamMember?.full_name || churchMember?.full_name || churchMember?.name || 'Integrante';
}

export function resolvePhoto(teamMember = {}, churchMember = {}) {
  return teamMember?.photo_url || churchMember?.photo_url || '';
}

export function getScheduleReferenceTime(schedule = {}, team) {
  const rawTime = schedule?.[team.timeField] || (team.fallbackTimeField ? schedule?.[team.fallbackTimeField] : '') || schedule?.start_time || '';
  const rawDate = dateOnly(schedule.date);
  if (!rawDate || !rawTime) return null;
  const [hours = '0', minutes = '0'] = String(rawTime).split(':');
  const parsed = new Date(`${rawDate}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function getCheckinTiming(schedule, team, checkedAt = new Date()) {
  const reference = getScheduleReferenceTime(schedule, team);
  if (!reference) return { status: 'late', label: 'Check-in realizado', points: POINT_RULES.checkinLate };

  const checkinDate = checkedAt instanceof Date ? checkedAt : new Date(checkedAt);
  const diffMinutes = Math.floor((checkinDate.getTime() - reference.getTime()) / 60000);
  if (diffMinutes < 0) return { status: 'early', label: 'Antes do horário', points: POINT_RULES.checkinEarly };
  if (diffMinutes <= 5) return { status: 'on_time', label: 'No horário', points: POINT_RULES.checkinOnTime };
  return { status: 'late', label: 'Após tolerância', points: POINT_RULES.checkinLate };
}

export function getScheduleCheckinWindow(schedule, team) {
  const reference = getScheduleReferenceTime(schedule, team);
  if (!reference) return null;

  return {
    start: new Date(reference.getTime() - CHECKIN_TIME_WINDOW_MINUTES.before * 60000),
    end: new Date(reference.getTime() + CHECKIN_TIME_WINDOW_MINUTES.after * 60000)
  };
}

export function validateCheckinTimeWindow(schedule, team, checkedAt = new Date()) {
  const window = getScheduleCheckinWindow(schedule, team);
  if (!window) return { allowed: true };

  const date = checkedAt instanceof Date ? checkedAt : new Date(checkedAt);
  if (Number.isNaN(date.getTime())) return { allowed: false, message: 'Horário do check-in inválido.' };

  if (date < window.start) {
    return { allowed: false, message: 'Check-in ainda não liberado para esta escala.' };
  }

  if (date > window.end) {
    return { allowed: false, message: 'Janela de check-in encerrada para esta escala.' };
  }

  return { allowed: true };
}

function parseScheduleDeadline(schedule = {}) {
  const rawDate = dateOnly(schedule.date);
  if (!rawDate) return null;

  const rawTime = schedule.end_time || '';
  if (rawTime) {
    const [hours = '23', minutes = '59'] = String(rawTime).split(':');
    const parsed = new Date(`${rawDate}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:59`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(`${rawDate}T23:59:59`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function isScheduleConfirmationClosed(schedule = {}, now = new Date()) {
  const deadline = parseScheduleDeadline(schedule);
  if (!deadline) return false;

  const referenceDate = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(referenceDate.getTime())) return false;

  return referenceDate.getTime() > deadline.getTime();
}

export function isScheduleConfirmationWithinDeadline(schedule = {}, confirmedAt) {
  if (!confirmedAt) return true;

  const confirmedDate = confirmedAt instanceof Date ? confirmedAt : new Date(confirmedAt);
  if (Number.isNaN(confirmedDate.getTime())) return false;

  return !isScheduleConfirmationClosed(schedule, confirmedDate);
}

const toRadians = (value) => value * Math.PI / 180;

export function calculateDistanceMeters(origin, destination) {
  const originLatitude = Number(origin?.latitude);
  const originLongitude = Number(origin?.longitude);
  const destinationLatitude = Number(destination?.latitude);
  const destinationLongitude = Number(destination?.longitude);

  if ([originLatitude, originLongitude, destinationLatitude, destinationLongitude].some((value) => Number.isNaN(value))) {
    return null;
  }

  const earthRadiusMeters = 6371000;
  const latitudeDelta = toRadians(destinationLatitude - originLatitude);
  const longitudeDelta = toRadians(destinationLongitude - originLongitude);
  const a = Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(toRadians(originLatitude)) *
    Math.cos(toRadians(destinationLatitude)) *
    Math.sin(longitudeDelta / 2) ** 2;

  return 2 * earthRadiusMeters * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function validateCheckinLocation(position, churchInfo, radiusMeters = CHECKIN_LOCATION_RADIUS_METERS) {
  const churchLatitude = Number(churchInfo?.latitude);
  const churchLongitude = Number(churchInfo?.longitude);
  if (Number.isNaN(churchLatitude) || Number.isNaN(churchLongitude)) {
    return {
      allowed: false,
      message: 'Localização da igreja não configurada. Informe latitude e longitude em Admin Igreja.'
    };
  }

  const distanceMeters = calculateDistanceMeters(
    { latitude: position?.latitude, longitude: position?.longitude },
    { latitude: churchLatitude, longitude: churchLongitude }
  );

  if (distanceMeters === null) {
    return { allowed: false, message: 'Não foi possível validar sua localização.' };
  }

  if (distanceMeters > radiusMeters) {
    return {
      allowed: false,
      distanceMeters,
      message: `Check-in permitido apenas no local da igreja. Você está a aproximadamente ${Math.round(distanceMeters)}m.`
    };
  }

  return { allowed: true, distanceMeters };
}

function parseScheduleDateTime(dateValue, timeValue) {
  const rawDate = dateOnly(dateValue);
  if (!rawDate || !timeValue) return null;
  const [hours = '0', minutes = '0'] = String(timeValue).split(':');
  const parsed = new Date(`${rawDate}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function isSchedulePastWithoutCheckin(schedule = {}, team, now = new Date()) {
  const endTime = parseScheduleDateTime(schedule.date, schedule.end_time);
  if (endTime) {
    return now.getTime() > endTime.getTime() + 15 * 60000;
  }

  const reference = getScheduleReferenceTime(schedule, team);
  if (reference) {
    return now.getTime() > reference.getTime() + 6 * 60 * 60000;
  }

  const scheduleDate = dateOnly(schedule.date);
  const today = localDateOnly(now);
  return !!scheduleDate && scheduleDate < today;
}

export function buildQrPayload(token) {
  const encodedToken = encodeURIComponent(token);
  return `${MOBILE_DEEP_LINK_SCHEME}://ranking-mbv?checkin=${encodedToken}`;
}

export function buildRankingCheckinUrl(token, options = {}) {
  const url = new URL(RANKING_CHECKIN_PATH, RANKING_CHECKIN_URL_ORIGIN);
  url.searchParams.set('checkin', token);
  if (options.openAttempted) {
    url.searchParams.set('mbv_open', '1');
  }
  return url.toString();
}

export function buildRankingCheckinIntentUrl(token, options = {}) {
  const webUrl = buildRankingCheckinUrl(token, options);
  const encodedToken = encodeURIComponent(token);
  const fallbackUrl = encodeURIComponent(webUrl);
  return `intent://ranking-mbv?checkin=${encodedToken}#Intent;scheme=${MOBILE_DEEP_LINK_SCHEME};package=${ANDROID_APP_PACKAGE};S.browser_fallback_url=${fallbackUrl};end`;
}

export function parseQrPayload(value) {
  const rawValue = String(value || '').trim();

  try {
    const parsedUrl = new URL(rawValue);
    const token = parsedUrl.searchParams.get('checkin') || parsedUrl.searchParams.get('token');
    if (token) return { type: 'mbv-ranking-checkin', token: token.trim() };
  } catch (_error) {
    // Not a URL; continue with JSON and raw token fallbacks.
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (parsed?.type === 'mbv-ranking-checkin' && parsed?.token) return parsed;
  } catch (_error) {
    // Accept raw token as a fallback for manual entry and older printed codes.
  }
  return { type: 'mbv-ranking-checkin', token: rawValue };
}

export function generateQrToken() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `mbv-${crypto.randomUUID()}`;
  }
  return `mbv-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function getQrImageUrl(token, size = 420) {
  const data = encodeURIComponent(buildQrPayload(token));
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=18&data=${data}`;
}

function isOnOrAfterDate(value, startDate) {
  if (!startDate) return true;
  const itemDate = dateOnly(value);
  return !!itemDate && itemDate >= startDate;
}

export async function loadRankingData() {
  const [churchMembers, checkins, adjustments, qrCodes, settings, churchInfos, ...teamPayloads] = await Promise.all([
    base44.entities.Member.list('-created_date', 2000).catch(() => []),
    base44.entities.RankingMBVCheckin.list('-created_date', 2000).catch(() => []),
    base44.entities.RankingMBVAdjustment.list('-created_date', 2000).catch(() => []),
    base44.entities.RankingMBVQrCode.list('-created_date', 2000).catch(() => []),
    base44.entities.RankingMBVSetting.list('-created_date', 2000).catch(() => []),
    base44.entities.ChurchInfo.list('-created_date', 1).catch(() => []),
    ...RANKING_TEAMS.flatMap((team) => [
      base44.entities[team.memberEntity].list('-created_date', 2000).catch(() => []),
      base44.entities[team.scheduleEntity].list('-date', 2000).catch(() => [])
    ])
  ]);

  const churchById = new Map(churchMembers.map((member) => [member.id, member]));
  const rankingStartSetting = (settings || []).find((setting) => setting.key === RANKING_START_SETTING_KEY);
  const rankingStartDate = dateOnly(rankingStartSetting?.value || rankingStartSetting?.start_date);
  const filteredCheckins = (checkins || []).filter((checkin) =>
    isOnOrAfterDate(checkin.schedule_date || checkin.checked_at || checkin.created_date, rankingStartDate)
  );
  const filteredAdjustments = (adjustments || []).filter((adjustment) =>
    isOnOrAfterDate(adjustment.created_date, rankingStartDate)
  );
  const teams = {};

  RANKING_TEAMS.forEach((team, index) => {
    const members = teamPayloads[index * 2] || [];
    const schedules = (teamPayloads[index * 2 + 1] || []).filter((schedule) =>
      isOnOrAfterDate(schedule.date, rankingStartDate)
    );
    teams[team.key] = { ...team, members, schedules };
  });

  return {
    churchMembers,
    churchById,
    checkins: filteredCheckins,
    adjustments: filteredAdjustments,
    qrCodes,
    settings,
    rankingStartDate,
    rankingStartSetting,
    churchInfo: churchInfos[0] || null,
    teams
  };
}

export function buildRankingRows({ team, members = [], schedules = [], checkins = [], adjustments = [], churchById = new Map() }) {
  const rows = members.map((teamMember) => {
    const churchMember = teamMember.member_id ? churchById.get(teamMember.member_id) : null;
    const memberCheckins = checkins.filter((checkin) => checkin.team_key === team.key && samePerson(teamMember, checkin));
    const memberAdjustments = adjustments.filter((adjustment) => adjustment.team_key === team.key && samePerson(teamMember, adjustment));
    const history = [];
    let scheduledPoints = 0;
    let confirmedPoints = 0;
    let checkinPoints = 0;
    let adjustmentPoints = 0;

    schedules.forEach((schedule) => {
      const scheduledMember = (schedule.members || []).find((item) => samePerson(teamMember, item));
      if (!scheduledMember) return;

      const scheduleCheckin = memberCheckins.find((checkin) => checkin.schedule_id === schedule.id);
      const missedWithoutCheckin = !scheduleCheckin && isSchedulePastWithoutCheckin(schedule, team);

      if (missedWithoutCheckin) {
        history.push({
          type: 'missed',
          date: schedule.date,
          title: schedule.title || 'Escala',
          label: 'Falta sem check-in',
          points: 0
        });
        return;
      }

      scheduledPoints += POINT_RULES.scheduled;
      history.push({
        type: 'scheduled',
        date: schedule.date,
        title: schedule.title || 'Escala',
        label: scheduleCheckin ? 'Escalado e presente' : 'Escalado',
        points: POINT_RULES.scheduled
      });

      if (scheduledMember.confirmed && isScheduleConfirmationWithinDeadline(schedule, scheduledMember.confirmed_at)) {
        confirmedPoints += POINT_RULES.confirmed;
        history.push({
          type: 'confirmed',
          date: scheduledMember.confirmed_at || schedule.date,
          title: schedule.title || 'Escala',
          label: 'Presença confirmada',
          points: POINT_RULES.confirmed
        });
      }
    });

    memberCheckins.forEach((checkin) => {
      const points = Number(checkin.points || 0);
      checkinPoints += points;
      history.push({
        type: 'checkin',
        date: checkin.checked_at || checkin.created_date,
        title: checkin.schedule_title || 'Check-in',
        label: checkin.timing_label || 'Check-in',
        points
      });
    });

    memberAdjustments.forEach((adjustment) => {
      const points = Number(adjustment.points || 0);
      adjustmentPoints += points;
      history.push({
        type: 'adjustment',
        date: adjustment.created_date,
        title: adjustment.reason || 'Correção administrativa',
        label: points >= 0 ? 'Ajuste positivo' : 'Ajuste negativo',
        points
      });
    });

    const total = scheduledPoints + confirmedPoints + checkinPoints + adjustmentPoints;

    return {
      id: teamMember.id,
      member_id: teamMember.member_id || null,
      email: teamMember.email || teamMember.member_email || churchMember?.email || '',
      name: resolveDisplayName(teamMember, churchMember),
      photo_url: resolvePhoto(teamMember, churchMember),
      total,
      scheduledPoints,
      confirmedPoints,
      checkinPoints,
      adjustmentPoints,
      scheduledCount: schedules.filter((schedule) => (schedule.members || []).some((item) => samePerson(teamMember, item))).length,
      confirmedCount: schedules.filter((schedule) => (schedule.members || []).some((item) =>
        samePerson(teamMember, item) &&
        item.confirmed &&
        isScheduleConfirmationWithinDeadline(schedule, item.confirmed_at)
      )).length,
      checkinCount: memberCheckins.length,
      history: history.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))
    };
  });

  return rows.sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, 'pt-BR'));
}

export function findMemberTeams(user, churchMember, teams) {
  return RANKING_TEAMS.map((team) => {
    const members = teams?.[team.key]?.members || [];
    const teamMember = members.find((member) => {
      if (churchMember?.id && member.member_id === churchMember.id) return true;
      if (user?.email && normalize(member.email || member.member_email) === normalize(user.email)) return true;
      return false;
    });
    return teamMember ? { team, teamMember } : null;
  }).filter(Boolean);
}

export function findTodayScheduleForMember(team, schedules, teamMember, today = new Date()) {
  return findTodaySchedulesForMember(team, schedules, teamMember, today)[0] || null;
}

export function findTodaySchedulesForMember(team, schedules, teamMember, today = new Date()) {
  const todayKey = localDateOnly(today);
  return (schedules || [])
    .filter((schedule) => dateOnly(schedule.date) === todayKey)
    .filter((schedule) => (schedule.members || []).some((member) => samePerson(teamMember, member)))
    .sort((left, right) => {
      const leftReference = getScheduleReferenceTime(left, team);
      const rightReference = getScheduleReferenceTime(right, team);
      const leftTime = leftReference ? leftReference.getTime() : Number.POSITIVE_INFINITY;
      const rightTime = rightReference ? rightReference.getTime() : Number.POSITIVE_INFINITY;
      return leftTime - rightTime || String(left.title || '').localeCompare(String(right.title || ''), 'pt-BR');
    });
}
